// All imports remain the same
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { fetchPeriods, fetchTeacherTimetable } from "./redux/myTimetableSlice";
import { fetchSessionsThunk } from "../../AcademicCalendar/redux/academicCalendarSlice";
import "./ShowTimeTable.css";
import Popup from "../../EventOrganiser/Popup";
import { fetchTeachers } from "../../SchoolModule/teachers/redux/teacherSlice";
import { getMasterDataByUserId } from "../../../pages/masterData/redux/masterDataThunk";
import userStore from "../../../zustand/userStore";
import { setAppdAiUser } from "../../../utils/appdAiHelpers";

export default function ShowTimeTable() {
  const dispatch = useDispatch();

  const { teacherPeriods, periods, loading, error } = useSelector(
    (state) => state.myTimetable
  );
  const sessions = useSelector((state) => state.academicCalendar.sessions);
  const {
    teachers,
    loading: teachersLoading,
    error: teachersError,
  } = useSelector((state) => state.teachers);
  const masterData = useSelector((state) => state.masterData);
  const schoolId = masterData?.school_id;

  const [selectedTeacherId, setSelectedTeacherId] = useState("");
  const [currentWeekStart, setCurrentWeekStart] = useState(null);
  const [sessionBounds, setSessionBounds] = useState({
    start: null,
    end: null,
  });
  const [selectedDate, setSelectedDate] = useState("");
  const [dateError, setDateError] = useState("");
  const [popupInfo, setPopupInfo] = useState(null);
  const userData = userStore((state) => state.user);

  useEffect(() => {
    dispatch(fetchPeriods());
    dispatch(fetchSessionsThunk());
    dispatch(getMasterDataByUserId(userData?.id));
  }, [dispatch]);

  useEffect(() => {
    // if (userData) setAppdAiUser(userData, "Academic Calender View");
    if (userData) setAppdAiUser(userData, "demo");
  }, []);

  useEffect(() => {
    if (schoolId) {
      dispatch(fetchTeachers());
    }
  }, [dispatch, schoolId]);

  useEffect(() => {
    if (selectedTeacherId) {
      dispatch(fetchTeacherTimetable(selectedTeacherId));
    }
  }, [dispatch, selectedTeacherId]);

  useEffect(() => {
    if (!sessions?.length) return;

    const session = sessions[0];
    const today = moment();
    const sessionStart = moment(session.start_date);
    const sessionEnd = moment(session.end_date);

    let weekStart;
    if (today.isBefore(sessionStart)) {
      weekStart = sessionStart.clone().startOf("week").add(1, "day");
    } else if (today.isAfter(sessionEnd)) {
      weekStart = sessionEnd.clone().startOf("week").add(1, "day");
    } else {
      weekStart = today.clone().startOf("week").add(1, "day");
    }

    setCurrentWeekStart(weekStart);
    setSessionBounds({ start: sessionStart, end: sessionEnd });
  }, [sessions]);

  const handlePrevWeek = () => {
    const newWeekStart = moment(currentWeekStart).subtract(7, "days");
    if (newWeekStart.isSameOrAfter(sessionBounds.start)) {
      setCurrentWeekStart(newWeekStart);
    }
  };

  const handleNextWeek = () => {
    const newWeekStart = moment(currentWeekStart).add(7, "days");
    if (newWeekStart.isSameOrBefore(sessionBounds.end)) {
      setCurrentWeekStart(newWeekStart);
    }
  };

  const isNavDisabled = (dir) => {
    if (!sessionBounds.start || !sessionBounds.end || !currentWeekStart)
      return true;
    const newDate = moment(currentWeekStart).add(
      dir === "next" ? 7 : -7,
      "days"
    );
    return dir === "next"
      ? newDate.isAfter(sessionBounds.end)
      : newDate.isBefore(sessionBounds.start);
  };

  const handleDateChange = (e) => {
    const inputDate = moment(e.target.value);
    setSelectedDate(e.target.value);

    if (
      inputDate.isSameOrAfter(sessionBounds.start, "day") &&
      inputDate.isSameOrBefore(sessionBounds.end, "day")
    ) {
      setCurrentWeekStart(inputDate.clone().startOf("week").add(1, "day"));
      setDateError("");
    } else {
      setDateError("Selected date is outside the session period.");
    }
  };

  const getValidDays = () => {
    if (!sessionBounds.start || !sessionBounds.end || !currentWeekStart)
      return [];

    const start = moment.max(moment(currentWeekStart), sessionBounds.start);
    const end = moment.min(
      moment(currentWeekStart).add(6, "days"),
      sessionBounds.end
    );

    const validDays = [];
    let current = start.clone();
    while (current.isSameOrBefore(end)) {
      const weekday = current.format("dddd");
      if (
        [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
        ].includes(weekday)
      ) {
        validDays.push({ name: weekday, date: current.clone() });
      }
      current.add(1, "day");
    }
    return validDays;
  };

  const getCellContent = (day, period_id) => {
    const data = teacherPeriods?.periods || [];
    const entry = data.find(
      (item) => item.day === day && item.period_id === period_id
    );

    return entry
      ? {
        subject: entry.subject_name,
        section: entry.section_name,
        filled: true,
      }
      : { subject: "", section: "", filled: false };
  };

  const getPeriodStyle = (type, name) => {
    if (name.toLowerCase().includes("lunch")) return "#ffebee";
    if (type === "assembly") return "#e3f2fd";
    if (type === "break") return "#fff8e1";
    return "#e8f5e9";
  };

  const formatTime = (timeStr) => moment(timeStr, "HH:mm:ss").format("h:mm A");

  if (sessions.length === 0) {
    return (
      <div className="alert alert-warning">
        Please enter session details in <strong>Academic Calendar</strong>.
      </div>
    );
  }

  if (!currentWeekStart) return <p>Loading session details...</p>;

  return (
    <div className="timetable-container px-5">
      {/* Teacher Dropdown */}
      <div className="filter-container mb-3">
        <label>
          Teacher:
          <select
            className="form-select"
            value={selectedTeacherId}
            onChange={(e) => {
              setSelectedTeacherId(e.target.value);
            }}
          >
            <option value="">Select a teacher</option>
            {/* {teachers?.map((teacher) => (
              <option key={teacher.teacher_id} value={teacher.teacher_id}>
                {teacher.name}
              </option>
            ))} */}
            {Array.isArray(teachers) && teachers.length > 0 ? (
              teachers.map((teacher) => (
                <option key={teacher.teacher_id} value={teacher.teacher_id}>
                  {teacher.name}
                </option>
              ))
            ) : (
              <option disabled>No teachers found</option>
            )}
          </select>
        </label>
      </div>

      {/* Week Navigation */}
      <div
        className="mt-2 d-flex align-items-center mx-auto gap-3"
        style={{ width: "fit-content" }}
      >
        <button
          className="nav-button"
          onClick={handlePrevWeek}
          disabled={isNavDisabled("prev")}
        >
          ⬅️
        </button>
        <h2 className="timetable-title">
          Weekly Time Table: {currentWeekStart.format("DD MMM")} -{" "}
          {moment(currentWeekStart).add(5, "days").format("DD MMM")}
        </h2>
        <button
          className="nav-button"
          onClick={handleNextWeek}
          disabled={isNavDisabled("next")}
        >
          ➡️
        </button>
      </div>
      {/* Date Picker */}
      <div className="date-picker-container">
        <div>
          <label>Select a Date: </label>
          <input
            className="select-control"
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            min={sessionBounds.start?.format("YYYY-MM-DD")}
            max={sessionBounds.end?.format("YYYY-MM-DD")}
          />
        </div>
        {dateError && <p className="error-text">{dateError}</p>}
      </div>

      {/* Error & Loader */}
      {teachersLoading && <p>Loading teachers...</p>}
      {loading && <p>Loading timetable...</p>}
      {error && <p className="error-text">{error}</p>}

      {/* Timetable Display */}
      {!loading && !error && periods.length > 0 && (
        <div className="table-wrapper">
          <table className="timetable-table">
            <thead>
              <tr>
                <th className="header-cell">Day & Date</th>
                {periods.map((period) => (
                  <th
                    key={period.period_id}
                    className="header-cell"
                    style={{
                      backgroundColor: getPeriodStyle(
                        period.period_type,
                        period.period_name
                      ),
                    }}
                  >
                    <div className="period-name">{period.period_name}</div>
                    <div className="period-time">
                      {formatTime(period.start_time)} -{" "}
                      {formatTime(period.end_time)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {getValidDays().map(({ name, date }) => (
                <tr key={name}>
                  <td className="day-cell">
                    <div>{name}</div>
                    <div className="day-date">{date.format("DD MMM YYYY")}</div>
                  </td>
                  {periods.map((period) => {
                    const { subject, section, filled } = getCellContent(
                      name,
                      period.period_id
                    );
                    return (
                      <td
                        key={period.period_id}
                        className={`period-cell ${filled ? "filled" : "empty"}`}
                        onClick={() =>
                          setPopupInfo({
                            date: date.format("DD MMM YYYY"),
                            day: name,
                            period: period.period_name,
                          })
                        }
                      >
                        <div className="subject">{subject}</div>
                        <div className="section">{section}</div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Popup */}
          {popupInfo && (
            <Popup
              title="Period Details"
              height="500px"
              maxWidth="1000px"
              onClose={() => setPopupInfo(null)}
              minHeight="500px"
            >
              <div>
                <h3>📅 Period Info</h3>
                <p>
                  <strong>Date:</strong> {popupInfo.date}
                </p>
                <p>
                  <strong>Day:</strong> {popupInfo.day}
                </p>
                <p>
                  <strong>Period:</strong> {popupInfo.period}
                </p>
                <button onClick={() => setPopupInfo(null)}>Close</button>
              </div>
            </Popup>
          )}
        </div>
      )}
    </div>
  );
}
